import Section from 'components/Section'
import { Typography, MediaImageStandard } from 'components/Contentful'
import { AchieveTheme, Grid, Paper } from '@achieve/ascend'
import { AchieveLink } from 'components/AchieveLink/AchieveLink'
import { BLOCKS } from '@contentful/rich-text-types'
import { useViewportSmallerThan } from 'utils/viewport'
import { BREAKPOINTS } from 'constants/viewport'
import { TitleDivider } from 'components/TitleDivider'
import { Carousel } from 'components/Carousels'
import { rewriteAssetUrl } from 'utils/conversions/urls'
import { mainProductConfig } from 'services/contentful/helpers'
import { useContext } from 'react'
import { LayoutContext } from 'providers/LayoutProvider'
import classNames from 'classnames'
import { FdrPage } from 'utils/environmentConfig'

function Card({
  idPrefix,
  item: { cardImage, cardText, cardLink },
  priority,
  index,
  listNameTracking,
}) {
  const isMobile = useViewportSmallerThan(BREAKPOINTS.xl)
  const [leadText, heading, supportingText] = cardText?.json?.content ?? []
  const headingText = typeof heading === 'string' ? heading : heading?.content[0].value
  const { linkText, linkHref, downloadFile } = cardLink || {}
  const assetUrl = rewriteAssetUrl(downloadFile?.fields?.file?.url)
  const { state: { siteEnv } = {} } = useContext(LayoutContext)
  const CardContent = (
    <Grid
      container
      direction={{ xs: 'column', lg: 'row' }}
      justifyContent={{ xs: 'flex-start', lg: 'flex-start' }}
      alignItems={{ xs: 'stretch', lg: 'center' }}
      className={'lg:h-[339px]'}
    >
      <Grid
        item
        xs={12}
        lg={7}
        className={classNames(
          'w-[inherit] h-[inherit] flex justify-center items-center pt-0 pb-8 px-0 lg:w-[calc(100%_*_7_/_12)] lg:pl-0 lg:pr-8 lg:py-0',
          '[&_span]:box-border [&_span]:block [&_span]:overflow-hidden [&_span]:w-[initial] [&_span]:h-[initial] [&_span]:opacity-100 [&_span]:absolute [&_span]:m-0 [&_span]:p-0 [&_span]:border-0 [&_span]:inset-0 [&_span]:[background:none]',
          '[&_img]:absolute [&_img]:box-border [&_img]:block [&_img]:w-0 [&_img]:h-0 [&_img]:min-w-full [&_img]:max-w-full [&_img]:min-h-full [&_img]:max-h-full [&_img]:object-cover [&_img]:object-[center_top] [&_img]:m-auto [&_img]:p-0 [&_img]:border-[none] [&_img]:inset-0'
        )}
      >
        <div className={'relative max-h-full max-w-full w-full h-[341px]'}>
          <span>
            <MediaImageStandard
              content={cardImage}
              objectFit="cover"
              height={isMobile ? 300 : 341}
              objectPosition="top"
              priority={priority}
              className={'rounded-xl'}
            />
          </span>
        </div>
      </Grid>
      <Grid container item xs={12} lg={5}>
        <Grid item xs={12}>
          <Typography
            content={leadText}
            data-testid={`${idPrefix}-lead-text`}
            className={'bodySm lg:bodyLg text-neutral-300 mb-2 lg:text-neutral-500 lg:mb-4'}
            variant={isMobile ? 'bodySm' : 'bodyLg'}
          />
        </Grid>
        <Grid item xs={12}>
          {assetUrl ? (
            <Typography
              content={heading}
              data-testid={`${idPrefix}-heading-text`}
              variant={isMobile ? 'bodyLg' : 'headingMd'}
              className={'bodyLg lg:headingMd text-neutral-900 inline'}
              fontWeight="medium"
            />
          ) : (
            <>
              <AchieveLink
                href={linkHref}
                className={'no-underline hover:underline hover:decoration-neutral-900'}
                track={{
                  list_name: 'Recommended Cards Carousel Link',
                  click_position: index,
                  click_text: `${
                    mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']
                  } | ${headingText}`,
                  click_id: headingText,
                }}
              >
                <Typography
                  content={heading}
                  data-testid={`${idPrefix}-heading-text`}
                  variant={isMobile ? 'bodyLg' : 'headingMd'}
                  className={'bodyLg lg:headingMd text-neutral-900 inline'}
                  fontWeight="medium"
                />
              </AchieveLink>
            </>
          )}
        </Grid>
        <Grid item xs={12}>
          <Typography
            content={supportingText}
            data-testid={`${idPrefix}-supporting-text`}
            fontWeight="regular"
            className={'text-neutral-900 mx-0 my-4 lg:text-neutral-900 lg:mt-6 lg:mb-4 lg:mx-0'}
            variant="bodySm"
          />
        </Grid>
        <Grid item xs={12}>
          {assetUrl ? (
            <AchieveLink
              href={assetUrl}
              target="blank"
              download
              className={'no-underline [&_p]:m-0'}
              track={{
                list_name: listNameTracking,
                click_position: index,
                click_text: `${mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']} | ${
                  cardLink?.title?.content[0]?.content[0]?.value
                }`,
                click_id: cardLink?.title?.content[0]?.content[0]?.value,
              }}
            >
              <Typography
                className="bodySm lg:bodyLg !font-medium lg:!font-bold"
                content={cardLink?.title}
                component="h3"
              />
            </AchieveLink>
          ) : (
            <AchieveLink
              href={linkHref}
              className={'no-underline [&_p]:m-0'}
              track={{
                list_name: listNameTracking,
                click_position: index,
                click_text: `${mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']} | ${
                  linkText?.json?.content?.[0]?.content[0]?.value
                }`,
                click_id: linkText?.json?.content?.[0]?.content[0]?.value,
                click_url: linkHref,
              }}
            >
              <Typography
                content={linkText}
                className="bodySm lg:bodyLg !font-medium lg:!font-bold"
                styleOverride={{
                  [BLOCKS.PARAGRAPH]: {
                    marginTop: '1em',
                  },
                }}
              />
            </AchieveLink>
          )}
        </Grid>
      </Grid>
    </Grid>
  )

  return isMobile ? (
    <>{CardContent}</>
  ) : (
    <Paper
      elevation={0}
      className={
        'border rounded-[10px] p-10 border-solid border-neutral-50 [.selected-item_&]:shadow-[0_3.3px_18px_-3px_rgb(16_24_40_/_9%),0_5px_6px_-2px_rgb(16_24_40_/_4%)]'
      }
    >
      <>{CardContent}</>
    </Paper>
  )
}

function PaperCarousel({
  title,
  carouselItems,
  theme,
  idPrefix = 'page',
  eyebrow,
  listNameTracking = null,
  priority = false,
}) {
  // we are using anything smaller than lg as our mobile for this one
  // for now though, we will make a medium screen resolution later
  // *** UPDATE 7/8/22 ***
  // The lg breakpoint was changed (now 1024px) and xl (1280) is closer to the old lg (1200).
  const isMobile = useViewportSmallerThan(BREAKPOINTS.lg)
  const sectionIdPrefix = `${idPrefix}-more-resources-carousel`
  const backgroundColor =
    theme?.theme[0] == 'light-blue'
      ? AchieveTheme?.ascend?.colors?.blue?.[50]
      : AchieveTheme?.ascend?.colors?.neutral?.[0]
  const { state: { siteEnv } = {} } = useContext(LayoutContext)
  return (
    <Section
      contain={false}
      className={'bg-blue-50 py-8 lg:py-16'}
      backgroundColor={backgroundColor}
    >
      <TitleDivider
        eyebrow={eyebrow}
        className={'mb-0 lg:mb-8'}
        title={title}
        data-testid={`${idPrefix}-title`}
        classNameTitleText={classNames('headingMd lg:displayLg', `${siteEnv === FdrPage ? '!font-bold px-[60px] lg:px-0': '!font-medium'}`)}
        classNameSubtitleText="bodySm lg:displaySm !font-light"
      />
      <Carousel
        carouselItems={carouselItems}
        itemComponent={Card}
        variant="preview"
        idPrefix={sectionIdPrefix}
        className={'shadow-selected'}
        currentClassName={'selected-item'}
        withIndicators={isMobile}
        classNameIndicators={'pt-8 [&>div]:bg-neutral-150 indicators'}
        classNameActiveDot={'[.indicators_&]:bg-[#30f] [.indicators_&]:opacity-100'}
        dataItem={{ listNameTracking, priority }}
      />
    </Section>
  )
}

export { PaperCarousel }
